import OpenApiConfiguration from 'api/OpenApiConfiguration';
import useRudderStackTrack from 'hooks/useRudderStackTrack';
import { useAtom, useSetAtom } from 'jotai';
import { useEffect } from 'react';
import { useQuery } from 'react-query';
import styled from 'styled-components';
import { ExperienceTagApi } from 'swagger/apis/experience-tag-api';
import { experienceSubTagOptionsAtom, selectedExperienceTagAtom } from '../filterStore';
import experienceIcons, { FishingIcon } from './ExpTileIcons';

const experiencesApi = new ExperienceTagApi(OpenApiConfiguration);
export const useGetSearchConfig = () => {
  const { data: { data } = {}, ...rest } = useQuery(
    ['domesticV3ConfigsSearchGet'],
    () => experiencesApi.domesticV3ConfigsSearchGet(),
    {
      enabled: true,
      refetchOnWindowFocus: false,
    }
  );
  const experienceTagData = data?.filters?.experience_tags || [];

  return {
    data,
    experienceTagData,
    ...rest,
  };
};

const ExperienceTiles = ({ experiencesToShow, location }) => {
  const rudderTrack = useRudderStackTrack();
  const [selectedExperienceTags, setSelectedExperienceTag] = useAtom(selectedExperienceTagAtom);
  const setExperienceSubTagOptions = useSetAtom(experienceSubTagOptionsAtom);

  const selectedLength = selectedExperienceTags?.length;
  useEffect(() => {
    if (selectedExperienceTags?.length > 0) {
      const thisTag = experiencesToShow.find((exp) => exp.key === selectedExperienceTags[0]);
      const newSubTags = thisTag?.sub_tags || [];
      setExperienceSubTagOptions(newSubTags);
    } else {
      // clear subtag data if no experience tag is selected
      setExperienceSubTagOptions([]);
    }
  }, [selectedLength]);

  const experienceKeys = experiencesToShow.map((exp) => exp.key).join(', ');

  useEffect(() => {
    rudderTrack('show', {
      component_type: 'button',
      object: 'experience_filter_tile',
      context: 'plp',
      filter_text: experienceKeys,
      search_location: location,
    });
  }, [experienceKeys]);

  const selectTile = (tag, label) => {
    // if it's selected we want to remove it from the experience tags
    // if a parent tag is removed, the subtags should also be removed
    if (selectedExperienceTags?.includes(tag)) {
      setSelectedExperienceTag([]);
    } else {
      setSelectedExperienceTag([tag]);
      const newSubTags = experiencesToShow.find((exp) => exp.key === tag)?.sub_tags || [];
      setExperienceSubTagOptions(newSubTags);
    }
    rudderTrack('click', {
      component_type: 'button',
      object: 'experience_filter_tile',
      context: 'plp',
      filter_text: label,
    });
  };

  return (
    <TilesWrapper>
      {experiencesToShow.map(({ key, display_name: label }) => (
        <Tile
          key={key}
          tag={key}
          selected={selectedExperienceTags?.includes(key)}
          label={label}
          onClick={() => selectTile(key, label)}
        />
      ))}
    </TilesWrapper>
  );
};

const TilesWrapper = styled.div`
  grid-row: 1;
  display: flex;
  flex-direction: row;
  column-gap: 12px;
  overflow: scroll;
  padding: 0 24px;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const Tile = ({ label, tag, onClick, selected }) => {
  const Icon = experienceIcons[tag] || FishingIcon;
  return (
    <TileButton className={selected && 'selected'} onClick={onClick}>
      <span>
        <Icon color={selected ? '#fff' : '#17263C'} />
      </span>
      {label}
    </TileButton>
  );
};

const TileButton = styled.button`
  border: 1px solid #dbdfe5;
  border-radius: 8px;
  padding: 12px 12px 8px 12px;
  min-width: 102px;
  min-height: 74px;
  display: flex;
  flex-direction: column;
  align-items: center;
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
  color: #17263c;

  span {
    margin-bottom: 4px;
    display: flex;
  }

  &:hover {
    background-color: #f8f9fa;
    border-color: #c3c7cc;
  }

  &.selected,
  &:active {
    color: #ffffff;
    background-color: #17263c;
    border-color: #434c5c;
  }
`;

export default ExperienceTiles;
